/**
 * Document Upload Portal - Apple-Style Minimalist Design
 *
 * Self-service portal for recruits to upload identification documents.
 * Supports Primary ID, Secondary ID (with front/back), and Other documents.
 * Features preview, delete, and replace functionality.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Plus, X, Check, Loader2, Eye, RefreshCw, Shield, Save, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { ID_OPTIONS, idRequiresBack, type IDType, type IDStatus } from '@/types/identification';
import { ComplianceFooter } from './ComplianceFooter';
import { SecureUploadCompanyProfileStatus } from './SecureUploadCompanyProfileStatus';

interface IDStatusInfo {
  status: IDStatus;
  rejectionReason?: string;
  type?: IDType;
  hasFront?: boolean;
  hasBack?: boolean;
}

interface UploadSession {
  entityType: string;
  entityId: string;
  otherDocuments?: OtherDocument[];
  firstName: string;
  lastName: string;
  requiredDocuments: string[];
  idStatus?: {
    primary: IDStatusInfo;
    secondary: IDStatusInfo;
  };
}

interface IDUploadState {
  type: IDType | null;
  frontFile: { name: string; uploadedAt: string } | null;
  backFile: { name: string; uploadedAt: string } | null;
  expanded: boolean;
}

interface OtherDocument {
  id: string;
  label: string;
  file: { name: string; uploadedAt: string } | null;
}

export const DocumentUploadPortal = () => {
  const navigate = useNavigate();
  const {
    companyProfile,
    loading: profileLoading,
    error: profileError,
    retry: retryProfile
  } = useCompanyProfile();
  const [session, setSession] = useState<UploadSession | null>(null);
  const [primaryId, setPrimaryId] = useState<IDUploadState>({
    type: null,
    frontFile: null,
    backFile: null,
    expanded: true
  });
  const [secondaryId, setSecondaryId] = useState<IDUploadState>({
    type: null,
    frontFile: null,
    backFile: null,
    expanded: false
  });
  const [otherDocuments, setOtherDocuments] = useState<OtherDocument[]>([]);
  const [uploading, setUploading] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOtherSection, setShowOtherSection] = useState(false);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isFullyApproved, setIsFullyApproved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    sessionStorage.removeItem('uploadSession');
    const load = async () => {
    try {
      const response = await fetch('/.netlify/functions/recruit-auth', { method: 'POST', headers: { 'Content-Type':'application/json' }, body: JSON.stringify({ action:'status' }) });
      if (!response.ok) {
throw new Error('Verification required');
}
      const parsed = await response.json() as UploadSession;
      if (cancelled) {
return;
}
      setSession(parsed);
      setOtherDocuments(parsed.otherDocuments ?? []);
      for (const [slot,setter] of [['primary',setPrimaryId],['secondary',setSecondaryId]] as const) {
        const status = parsed.idStatus?.[slot];
        setter(previous => ({...previous,type:status?.type ?? null,
          frontFile: status?.hasFront ? {name:'Saved front',uploadedAt:''} : null,
          backFile: status?.hasBack ? {name:'Saved back',uploadedAt:''} : null}));
      }

      // Check ID statuses to determine UI state
      const primaryStatus = parsed.idStatus?.primary?.status;
      const secondaryStatus = parsed.idStatus?.secondary?.status;
      const bothApproved = primaryStatus === 'approved' && secondaryStatus === 'approved';
      const hasRejected = primaryStatus === 'rejected' || secondaryStatus === 'rejected';

      // Check if documents were actually uploaded (not just default pending state)
      const primaryHasUploads = parsed.idStatus?.primary?.hasFront;
      const secondaryHasUploads = parsed.idStatus?.secondary?.hasFront;
      const completeSlot = (slot: 'primary' | 'secondary') => {
 const id = parsed.idStatus?.[slot]; return id?.hasFront && id.type && (!idRequiresBack(id.type) || id.hasBack);
};
      const hasPendingWithUploads = primaryHasUploads && secondaryHasUploads && completeSlot('primary') && completeSlot('secondary');

      // If both IDs are approved, show "Documents Approved" state
      if (bothApproved) {
        setIsFullyApproved(true);
        setIsSubmitted(true);
        return;
      }

      // If any ID is pending WITH ACTUAL UPLOADS and NONE are rejected,
      // show "Under Review" - recruit can't do anything until admin reviews
      if (hasPendingWithUploads && !hasRejected) {
        setIsSubmitted(true);
        return;
      }

      // If there are rejections, show the upload form for rejected IDs only
      if (hasRejected) {
        // Pre-populate ID types for rejected IDs so recruit knows what to re-upload
        if (primaryStatus === 'rejected' && parsed.idStatus?.primary?.type) {
          setPrimaryId(prev => ({
            ...prev,
            type: parsed.idStatus!.primary.type!,
            expanded: true // Auto-expand to show rejection banner
          }));
        }

        if (secondaryStatus === 'rejected' && parsed.idStatus?.secondary?.type) {
          setSecondaryId(prev => ({
            ...prev,
            type: parsed.idStatus!.secondary.type!,
            expanded: true // Auto-expand to show rejection banner
          }));
        }

      }

    } catch {
      if (!cancelled) {
 toast.error('Please verify your saved email to continue.'); navigate('/secureuploads/recruit');
}
    }
    };
    void load();
    return () => {
 cancelled = true;
};
  }, [navigate]);

  // Check if at least one ID is complete (for Save button)
  const isPrimaryComplete = primaryId.type && primaryId.frontFile &&
    (!idRequiresBack(primaryId.type) || primaryId.backFile);
  const isSecondaryComplete = secondaryId.type && secondaryId.frontFile &&
    (!idRequiresBack(secondaryId.type) || secondaryId.backFile);
  // canSave if any visible ID is complete
  const primaryNeedsWork = session?.idStatus?.primary?.status !== 'approved';
  const secondaryNeedsWork = session?.idStatus?.secondary?.status !== 'approved';
  const canSave = (primaryNeedsWork && isPrimaryComplete) || (secondaryNeedsWork && isSecondaryComplete);

  // Manual save - only allowed when at least one ID is complete
  const handleSaveProgress = useCallback(async () => {
    if (!session || !canSave) {
return;
}
    setIsSaving(true);
    try {
      const response = await fetch('/.netlify/functions/recruit-auth', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({action:'status'}) });
      if (!response.ok) {
throw new Error('Verification required');
}
      setLastSaved(new Date()); toast.success('Your uploaded documents are saved.');
    } catch {
 toast.error('Please verify your email again before continuing.'); navigate('/secureuploads/recruit');
} finally {
 setIsSaving(false);
}
  }, [session, canSave, navigate]);

  const transferTicket = async (action: 'upload_id' | 'upload_other') => {
    const response = await fetch('/.netlify/functions/recruit-auth', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'transfer',transfer_action:action})});
    if (!response.ok) {
throw new Error('Please verify your email again before uploading.');
}
    return (await response.json()).ticket as string;
  };

  // Preview document
  const handlePreview = useCallback(async (documentType: string) => {
    if (!session) {
return;
}

    setPreviewing(documentType);

    try {
      const response = await fetch(
        '/.netlify/functions/recruit-auth',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'document', document_type: documentType
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get preview URL');
      }

      const { url } = await response.json();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Unable to preview document');
    } finally {
      setPreviewing(null);
    }
  }, [session]);

  // Replace document (clear state to show upload zone again)
  const handleReplace = useCallback((slot: 'primary' | 'secondary', side: 'front' | 'back') => {
    const setter = slot === 'primary' ? setPrimaryId : setSecondaryId;
    setter(prev => ({
      ...prev,
      [side === 'front' ? 'frontFile' : 'backFile']: null
    }));
  }, []);

  const handleIdUpload = useCallback(async (
    slot: 'primary' | 'secondary',
    side: 'front' | 'back',
    file: File
  ) => {
    if (!session) {
return;
}

    const idState = slot === 'primary' ? primaryId : secondaryId;
    if (!idState.type) {
      toast.error('Please select an ID type first');
      return;
    }

    const uploadKey = `${slot}_${side}`;
    setUploading(uploadKey);

    try {
      const formData = new FormData();
      formData.append('ticket', await transferTicket('upload_id'));
      formData.append('recruitId', session.entityId);
      formData.append('idSlot', slot);
      formData.append('side', side);
      formData.append('idType', idState.type);
      formData.append('file', file);

      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/upload-recruit-id`,
        { method: 'POST', body: formData }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const fileInfo = { name: file.name, uploadedAt: new Date().toISOString() };
      const setter = slot === 'primary' ? setPrimaryId : setSecondaryId;
      setter(prev => ({
        ...prev,
        [side === 'front' ? 'frontFile' : 'backFile']: fileInfo
      }));

      toast.success(`${side === 'front' ? 'Front' : 'Back'} uploaded successfully`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(null);
    }
  }, [session, primaryId, secondaryId]);

  const handleOtherUpload = useCallback(async (docId: string, file: File) => {
    if (!session) {
return;
}

    const doc = otherDocuments.find(d => d.id === docId);
    if (!doc || !doc.label.trim()) {
      toast.error('Please enter a document description first');
      return;
    }

    setUploading(`other_${docId}`);

    try {
      const formData = new FormData();
      formData.append('ticket', await transferTicket('upload_other'));
      formData.append('recruitId', session.entityId);
      formData.append('label', doc.label.trim());
      formData.append('file', file);

      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/upload-recruit-other`,
        { method: 'POST', body: formData }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const uploaded = await response.json();
      setOtherDocuments(prev =>
        prev.map(d =>
          d.id === docId
            ? { ...d, id: uploaded.document.id, file: { name: file.name, uploadedAt: new Date().toISOString() } }
            : d
        )
      );

      toast.success('Document uploaded successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(null);
    }
  }, [session, otherDocuments]);

  const handleSubmitAll = async () => {
    if (!session) {
return;
}

    setIsSubmitting(true);

    try {
      const response = await fetch(
        '/.netlify/functions/recruit-auth',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'complete'
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to submit documents');
      }

      toast.success('Documents submitted successfully!');
      sessionStorage.removeItem('uploadSession');
      setTimeout(() => navigate('/secureuploads'), 2000);
    } catch {
      toast.error('Failed to submit documents. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (profileLoading || profileError || !companyProfile) {
    return (
      <SecureUploadCompanyProfileStatus
        loading={profileLoading}
        onRetry={retryProfile}
      />
    );
  }

  if (!session) {
    return null;
  }

  // Only show IDs that need re-upload (rejected) or haven't been uploaded yet (no status)
  // Don't show if pending (waiting for review) or approved (done)
  const primaryStatus = session?.idStatus?.primary?.status;
  const secondaryStatus = session?.idStatus?.secondary?.status;
  const showPrimaryCard = primaryStatus !== 'approved';
  const showSecondaryCard = secondaryStatus !== 'approved';

  // allRequiredComplete checks only IDs that need attention
  // If one ID is already approved, we only check the remaining one
  const allRequiredComplete =
    (!showPrimaryCard || isPrimaryComplete) &&
    (!showSecondaryCard || isSecondaryComplete);
  const companyName = companyProfile.company_name;
  const hasRejectedIds = session?.idStatus?.primary?.status === 'rejected' ||
    session?.idStatus?.secondary?.status === 'rejected';

  // If already submitted or fully approved, show appropriate message
  if (isSubmitted) {
    return (
      <div className="h-screen overflow-y-auto bg-[#FBFBFD] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-semibold text-[#1D1D1F] tracking-tight">
              {isFullyApproved ? 'Documents Approved' : 'Documents Under Review'}
            </h1>
            <p className="text-lg text-[#86868B] mt-3">
              {isFullyApproved
                ? `Hi ${session.firstName}, your identification documents have been verified and approved. Thank you!`
                : `Hi ${session.firstName}, your identification documents have been submitted and are being reviewed.`
              }
            </p>
            <p className="text-sm text-[#86868B] mt-4">
              {isFullyApproved
                ? 'No further action is needed. Your recruiter will contact you with next steps.'
                : 'You will be notified once the review is complete. If you need to make changes, please contact your recruiter.'
              }
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-[#F5F5F7] rounded-xl">
                <Shield className="h-4 w-4 text-[#0071E3]" />
                <span className="text-xs font-medium text-[#1D1D1F]">Secure Portal</span>
              </div>
            </div>
          </div>
        </div>
        <ComplianceFooter companyProfile={companyProfile} />
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto bg-[#FBFBFD] flex flex-col">
      {/* Header */}
      <div className="pt-12 pb-8 px-4">
        <div className="max-w-xl mx-auto text-center">
          <h1 className="text-3xl font-semibold text-[#1D1D1F] tracking-tight">
            Hi {session.firstName}!
          </h1>
          <p className="text-lg text-[#86868B] mt-2">
            Welcome to {companyName} Secure Portal
          </p>

          {/* Action Required Banner for Rejected IDs */}
          {hasRejectedIds && (
            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <div className="flex items-center justify-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">
                  Action Required: Please review and re-upload the documents marked below
                </span>
              </div>
            </div>
          )}

          <div className="mt-6">
            <h2 className="text-2xl font-semibold text-[#1D1D1F] tracking-tight">
              Identity Documents
            </h2>
            <p className="text-base text-[#86868B] mt-1">
              {showPrimaryCard && showSecondaryCard
                ? 'Two forms of identification required'
                : 'One more document needed'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 pb-8">
        <div className="max-w-xl mx-auto space-y-6">
          {/* Primary ID Card - only show if not already approved */}
          {showPrimaryCard && (
            <IDCard
              number={1}
              title="Primary ID"
              subtitle="Driver's license or passport"
              idState={primaryId}
              onTypeChange={(type) => setPrimaryId(prev => ({
                ...prev,
                type,
                frontFile: null,
                backFile: null
              }))}
              onUpload={(side, file) => handleIdUpload('primary', side, file)}
              onToggle={() => setPrimaryId(prev => ({ ...prev, expanded: !prev.expanded }))}
              onPreview={(side) => handlePreview(`primary_${side}`)}
              onReplace={(side) => handleReplace('primary', side)}
              uploading={uploading}
              previewing={previewing}
              uploadPrefix="primary"
              isComplete={!!isPrimaryComplete}
              rejectionInfo={session?.idStatus?.primary}
            />
          )}

          {/* Secondary ID Card - only show if not already approved */}
          {showSecondaryCard && (
            <IDCard
              number={showPrimaryCard ? 2 : 1}
              title="Secondary ID"
              subtitle="Social Security card or other ID"
              idState={secondaryId}
              onTypeChange={(type) => setSecondaryId(prev => ({
                ...prev,
                type,
                frontFile: null,
                backFile: null,
                expanded: true
              }))}
              onUpload={(side, file) => handleIdUpload('secondary', side, file)}
              onToggle={() => setSecondaryId(prev => ({ ...prev, expanded: !prev.expanded }))}
              onPreview={(side) => handlePreview(`secondary_${side}`)}
              onReplace={(side) => handleReplace('secondary', side)}
              uploading={uploading}
              previewing={previewing}
              uploadPrefix="secondary"
              isComplete={!!isSecondaryComplete}
              rejectionInfo={session?.idStatus?.secondary}
            />
          )}

          {/* Other Documents Section */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div
              className="flex items-center justify-between p-8 cursor-pointer"
              onClick={() => setShowOtherSection(!showOtherSection)}
            >
              <div>
                <h2 className="text-xl font-semibold text-[#1D1D1F]">
                  Other Documents
                </h2>
                <p className="text-[#86868B] text-sm mt-1">
                  Optional supporting documents
                </p>
              </div>
              <span className="text-sm font-medium text-[#86868B] bg-[#F5F5F7] px-3 py-1 rounded-full">
                Optional
              </span>
            </div>

            {showOtherSection && (
              <div className="px-8 pb-8 space-y-4">
                {otherDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="p-4 bg-[#F5F5F7] rounded-xl"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <input
                        type="text"
                        placeholder="Document description..."
                        value={doc.label}
                        onChange={(e) => {
                          setOtherDocuments(prev =>
                            prev.map(d => d.id === doc.id ? { ...d, label: e.target.value } : d)
                          );
                        }}
                        disabled={!!doc.file}
                        className="flex-1 px-4 py-3 bg-white border-0 rounded-xl text-[#1D1D1F] placeholder-[#86868B] focus:ring-2 focus:ring-[#0071E3] focus:outline-none disabled:bg-[#F5F5F7] disabled:text-[#1D1D1F]"
                      />
                      {!doc.file && (
                        <button
                          onClick={() => setOtherDocuments(prev => prev.filter(d => d.id !== doc.id))}
                          className="p-3 text-[#86868B] hover:text-[#1D1D1F] transition-colors"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      )}
                    </div>

                    {doc.file ? (
                      <UploadedFileWithActions
                        name={doc.file.name}
                        label={doc.label}
                        onPreview={() => handlePreview(`other_${doc.id}`)}
                        onReplace={() => {
                          setOtherDocuments(prev =>
                            prev.map(d => d.id === doc.id ? { ...d, file: null } : d)
                          );
                        }}
                        isPreviewing={previewing === `other_${doc.id}`}
                      />
                    ) : (
                      <UploadZone
                        onUpload={(file) => handleOtherUpload(doc.id, file)}
                        disabled={!doc.label.trim()}
                        isUploading={uploading === `other_${doc.id}`}
                        label="Upload document"
                      />
                    )}
                  </div>
                ))}

                <button
                  onClick={() => setOtherDocuments(prev => [
                    ...prev,
                    { id: crypto.randomUUID(), label: '', file: null }
                  ])}
                  className="w-full h-14 bg-[#F5F5F7] rounded-xl flex items-center justify-center gap-2 text-[#86868B] hover:bg-[#E8E8ED] transition-colors"
                >
                  <Plus className="h-5 w-5" />
                  Add document
                </button>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmitAll}
            disabled={!allRequiredComplete || isSubmitting || !!uploading}
            className="w-full py-4 bg-[#0071E3] text-white font-medium rounded-xl hover:bg-[#0077ED] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit'
            )}
          </button>

          {/* Save Progress Button */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={handleSaveProgress}
              disabled={!canSave || isSaving}
              className="flex items-center gap-2 px-4 py-2 text-[#86868B] hover:text-[#1D1D1F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span className="text-sm font-medium">Save Progress</span>
            </button>
            {lastSaved && (
              <span className="text-xs text-[#86868B]">
                Last saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
          </div>

          {/* Help Text */}
          {!canSave && (
            <p className="text-center text-sm text-[#86868B]">
              Complete at least one ID (front and back) to save your progress
            </p>
          )}
          {canSave && !allRequiredComplete && (
            <p className="text-center text-sm text-[#86868B]">
              Upload both forms of ID to submit
            </p>
          )}
        </div>
      </div>

      <ComplianceFooter companyProfile={companyProfile} />
    </div>
  );
};

// ============================================================================
// ID Card Component
// ============================================================================

interface IDCardProps {
  number: number;
  title: string;
  subtitle: string;
  idState: IDUploadState;
  onTypeChange: (type: IDType) => void;
  onUpload: (side: 'front' | 'back', file: File) => void;
  onToggle: () => void;
  onPreview: (side: 'front' | 'back') => void;
  onReplace: (side: 'front' | 'back') => void;
  uploading: string | null;
  previewing: string | null;
  uploadPrefix: string;
  isComplete: boolean;
  rejectionInfo?: IDStatusInfo;
}

const IDCard = ({
  number,
  title,
  subtitle,
  idState,
  onTypeChange,
  onUpload,
  onToggle,
  onPreview,
  onReplace,
  uploading,
  previewing,
  uploadPrefix,
  isComplete,
  rejectionInfo
}: IDCardProps) => {
  const needsBack = idState.type && idRequiresBack(idState.type);
  const isRejected = rejectionInfo?.status === 'rejected';
  const isApproved = rejectionInfo?.status === 'approved';

  return (
    <div className={`bg-white rounded-2xl shadow-sm overflow-hidden ${
      isRejected ? 'ring-2 ring-red-200' : ''
    }`}>
      {/* Rejection Banner */}
      {isRejected && rejectionInfo?.rejectionReason && (
        <div className="bg-red-50 border-b border-red-100 p-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-red-900">
                {title} Needs Attention
              </h3>
              <p className="text-sm text-red-700 mt-1">
                {rejectionInfo.rejectionReason}
              </p>
              <p className="text-xs text-red-600 mt-2 font-medium">
                Please upload a new document below
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Approved Banner */}
      {isApproved && (
        <div className="bg-green-50 border-b border-green-100 p-4">
          <div className="flex items-center gap-2">
            <Check className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-green-700">
              {title} has been approved
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div
        className="flex items-start justify-between p-8 cursor-pointer"
        onClick={onToggle}
      >
        <div>
          <h2 className="text-xl font-semibold text-[#1D1D1F]">{title}</h2>
          <p className="text-[#86868B] text-sm mt-1">{subtitle}</p>
        </div>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isApproved ? 'bg-green-100' : isRejected ? 'bg-red-100' : isComplete ? 'bg-green-100' : 'bg-[#F5F5F7]'
        }`}>
          {isApproved ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : isRejected ? (
            <AlertCircle className="h-4 w-4 text-red-600" />
          ) : isComplete ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <span className="text-sm font-medium text-[#1D1D1F]">{number}</span>
          )}
        </div>
      </div>

      {/* Content */}
      {idState.expanded && (
        <div className="px-8 pb-8 space-y-4">
          {/* ID Type Selector */}
          <select
            aria-label={`${title} type`}
            value={idState.type || ''}
            onChange={(e) => onTypeChange(e.target.value as IDType)}
            className="w-full px-4 py-3 bg-[#F5F5F7] border-0 rounded-xl text-[#1D1D1F] focus:ring-2 focus:ring-[#0071E3] focus:outline-none appearance-none cursor-pointer"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2386868B' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 1rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.25rem 1.25rem'
            }}
          >
            <option value="">Select ID type</option>
            {ID_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Upload Zones */}
          {idState.type && (
            <div className={`grid gap-4 ${needsBack ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {/* Front */}
              <div>
                {idState.frontFile ? (
                  <UploadedFileWithActions
                    name={idState.frontFile.name}
                    label="Front"
                    onPreview={() => onPreview('front')}
                    onReplace={() => onReplace('front')}
                    isPreviewing={previewing === `${uploadPrefix}_front`}
                  />
                ) : (
                  <UploadZone
                    onUpload={(file) => onUpload('front', file)}
                    isUploading={uploading === `${uploadPrefix}_front`}
                    label="Front"
                  />
                )}
              </div>

              {/* Back */}
              {needsBack && (
                <div>
                  {idState.backFile ? (
                    <UploadedFileWithActions
                      name={idState.backFile.name}
                      label="Back"
                      onPreview={() => onPreview('back')}
                      onReplace={() => onReplace('back')}
                      isPreviewing={previewing === `${uploadPrefix}_back`}
                    />
                  ) : (
                    <UploadZone
                      onUpload={(file) => onUpload('back', file)}
                      isUploading={uploading === `${uploadPrefix}_back`}
                      label="Back"
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Upload Zone Component
// ============================================================================

interface UploadZoneProps {
  onUpload: (file: File) => void;
  disabled?: boolean;
  isUploading: boolean;
  label: string;
}

const UploadZone = ({ onUpload, disabled, isUploading, label }: UploadZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || isUploading) {
return;
}

    const file = e.dataTransfer.files[0];
    if (file) {
onUpload(file);
}
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
onUpload(file);
}
    e.target.value = '';
  };

  return (
    <label
      onDragOver={(e) => {
 e.preventDefault(); setIsDragging(true);
}}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={`
        aspect-[4/3] bg-[#F5F5F7] rounded-xl flex flex-col items-center justify-center cursor-pointer transition-colors
        ${isDragging ? 'bg-[#E8E8ED] ring-2 ring-[#0071E3]' : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#E8E8ED]'}
      `}
    >
      <input
        type="file"
        accept="image/*,.pdf"
        onChange={handleFileSelect}
        disabled={disabled || isUploading}
        className="hidden"
      />
      {isUploading ? (
        <Loader2 className="h-6 w-6 text-[#0071E3] animate-spin" />
      ) : (
        <>
          <Upload className="h-6 w-6 text-[#86868B] mb-2" />
          <span className="text-sm text-[#86868B]">{label}</span>
        </>
      )}
    </label>
  );
};

// ============================================================================
// Uploaded File with Actions Component
// ============================================================================

interface UploadedFileWithActionsProps {
  name: string;
  label: string;
  onPreview: () => void;
  onReplace: () => void;
  isPreviewing: boolean;
}

const UploadedFileWithActions = ({
  name,
  label,
  onPreview,
  onReplace,
  isPreviewing
}: UploadedFileWithActionsProps) => (
  <div className="aspect-[4/3] bg-green-50 rounded-xl flex flex-col items-center justify-center border border-green-200 relative group">
    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mb-2">
      <Check className="h-5 w-5 text-green-600" />
    </div>
    <span className="text-sm font-medium text-green-700">{label}</span>
    <span className="text-xs text-green-600 mt-1 max-w-[80%] truncate px-2">{name}</span>

    {/* Action buttons - visible on hover */}
    <div className="absolute inset-0 bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
      <button
        onClick={(e) => {
 e.stopPropagation(); onPreview();
}}
        disabled={isPreviewing}
        className="p-3 bg-white rounded-xl text-[#1D1D1F] hover:bg-[#F5F5F7] transition-colors disabled:opacity-50"
        title="Preview"
      >
        {isPreviewing ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <Eye className="h-5 w-5" />
        )}
      </button>
      <button
        onClick={(e) => {
 e.stopPropagation(); onReplace();
}}
        className="p-3 bg-white rounded-xl text-[#1D1D1F] hover:bg-[#F5F5F7] transition-colors"
        title="Replace"
      >
        <RefreshCw className="h-5 w-5" />
      </button>
    </div>
  </div>
);

export default DocumentUploadPortal;
